import {
  Component,
  DestroyRef,
  ElementRef,
  Injector,
  OnInit,
  afterNextRender,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RouterLink } from '@angular/router';
import { finalize } from 'rxjs';

import { IconComponent } from '../../../shared/components/icon/icon.component';
import { PatientSearchModal } from '../../../shared/components/patient-search-modal/patient-search-modal';
import { ProgressSpinnerComponent } from '../../../shared/components/progress-spinner/progress-spinner.component';
import { PatientFilter } from '../../../shared/models/patient-filter';
import { formatDobNumeric } from '../../../shared/utils/date-time/date-time.utils';
import { logHttpError } from '../../../shared/utils/logging/http-error-logging.utils';
import {
  NOT_AVAILABLE,
  formatEmailAddress,
  formatPatientMrn,
  getPatientInitials,
} from '../../../shared/utils/patient-display/patient-display.utils';
import { PatientAccessRecord } from '../../models/patient-access-record';
import {
  PATIENT_ACCESS_MINIMUM_CRITERIA,
  PatientAccessSearchService,
} from '../../services/patient-access-search.service';
import { PatientAccessSearchStateService } from '../../services/patient-access-search-state.service';

/** One "Last name: Whitfield" chip summarising a populated criterion. */
interface CriterionChip {
  label: string;
  value: string;
}

/**
 * Patient Access results (Access & Controls → Search by Patient). Runs the criteria collected by
 * the shared search dialog and lists every matching patient, so staff can pick the right record
 * before managing its portal access.
 *
 * The criteria arrive in memory via {@link PatientAccessSearchStateService} rather than in the URL
 * — they are patient identifiers, and URLs get logged. That means a refresh or a pasted link
 * arrives with nothing to search, which this screen treats as a normal starting state and invites
 * a fresh search rather than reporting an error.
 */
@Component({
  selector: 'app-patient-access-results',
  standalone: true,
  imports: [RouterLink, IconComponent, ProgressSpinnerComponent, PatientSearchModal],
  templateUrl: './patient-access-results.html',
  styleUrl: './patient-access-results.scss',
})
export class PatientAccessResults implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly searchService = inject(PatientAccessSearchService);
  private readonly searchStateService = inject(PatientAccessSearchStateService);
  private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);

  /** Needed so `closeModal` can register an `afterNextRender` hook outside an injection context. */
  private readonly injector = inject(Injector);

  /** The element that opened the dialog, so closing can hand focus straight back to it. */
  private trigger: HTMLElement | null = null;

  /** The criteria the current results reflect; null before any search has run on this screen. */
  readonly criteria = signal<PatientFilter | null>(null);
  readonly results = signal<PatientAccessRecord[]>([]);
  /** Server-side total behind `results`; null until a search has completed. */
  readonly totalRecordCount = signal<number | null>(null);
  readonly isSearching = signal(false);
  readonly searchError = signal<string | null>(null);
  readonly hasSearched = signal(false);
  readonly isModalOpen = signal(false);

  /** Criteria the endpoint requires; the dialog gates on it so a doomed search isn't submitted. */
  readonly minimumCriteria = PATIENT_ACCESS_MINIMUM_CRITERIA;

  /**
   * True when the screen was reached without criteria (refresh, or a pasted link). Distinct from
   * "the search found nothing" — one invites a search, the other reports a result.
   */
  readonly hasCriteria = computed(() => this.criteria() !== null);

  /** The populated criteria, as chips, so staff can see exactly what produced these rows. */
  readonly criteriaChips = computed<CriterionChip[]>(() => {
    const filter = this.criteria();
    if (!filter) {
      return [];
    }

    return [
      { label: 'First name', value: filter.firstName },
      { label: 'Last name', value: filter.lastName },
      {
        label: 'Date of birth',
        value: filter.dateOfBirth ? formatDobNumeric(filter.dateOfBirth) : null,
      },
      { label: 'MRN', value: filter.medicalRecordNumber },
    ].filter((chip): chip is CriterionChip => chip.value !== null);
  });

  /**
   * True when the server matched more records than this page holds. The search takes a fixed page
   * with no paging UI, so without this the screen would report a truncated page as every match.
   */
  readonly isTruncated = computed(() => {
    const total = this.totalRecordCount();
    return total !== null && total > this.results().length;
  });

  readonly resultSummary = computed(() => {
    if (!this.hasSearched()) {
      return '';
    }
    const count = this.results().length;
    if (this.isTruncated()) {
      return `Showing ${count} of ${this.totalRecordCount()}`;
    }
    return `${count} patient${count === 1 ? '' : 's'} found`;
  });

  ngOnInit(): void {
    const pending = this.searchStateService.activeCriteria();
    if (!pending) {
      return;
    }

    this.criteria.set(pending);
    this.runSearch(pending);
  }

  /**
   * `trigger` is the button that opened the dialog, captured so closing can return focus to it.
   * Mirrors {@link AccessControls.openArea} — `FocusTrapDirective` only loops Tab within the
   * dialog and leaves the restore to the host, so nothing else picks this up.
   */
  openModal(trigger: EventTarget | null = null): void {
    this.trigger = trigger instanceof HTMLElement ? trigger : null;
    this.isModalOpen.set(true);
  }

  closeModal(): void {
    const trigger = this.trigger;
    this.trigger = null;
    this.isModalOpen.set(false);

    if (!trigger) {
      return;
    }

    // Hand focus back to the control that opened the dialog, so a keyboard or screen-reader user
    // resumes where they left off instead of being dropped at the top of the document.
    // `afterNextRender` runs after the render that removes the dialog — ordering-guaranteed rather
    // than a timer racing change detection — and is cancelled if the page is destroyed first.
    afterNextRender(() => trigger.focus(), { injector: this.injector });
  }

  /** Re-runs the search with criteria edited in the dialog, keeping the shared state in step. */
  onPatientSearch(filter: PatientFilter): void {
    // Committed only once the search can actually start. Returning after the criteria were stored
    // would leave the chips, and the dialog's prefill, describing a search that never ran —
    // captioning the previous patient's rows with the new criteria.
    if (this.isSearching()) {
      return;
    }

    // Cleared before closing so `closeModal` doesn't restore focus to the header button, which
    // `isSearching()` is about to disable. Focus goes to the results heading instead, which is
    // where the answer to this search appears.
    this.trigger = null;
    this.closeModal();
    this.searchStateService.setCriteria(filter);
    this.criteria.set(filter);
    this.runSearch(filter);

    afterNextRender(() => this.focusResultsHeading(), { injector: this.injector });
  }

  /**
   * Moves focus to the results heading after an applied search. It carries `tabindex="-1"` so it
   * can take programmatic focus without joining the tab order.
   */
  private focusResultsHeading(): void {
    this.elementRef.nativeElement.querySelector<HTMLElement>('#results-title')?.focus();
  }

  getInitials(record: PatientAccessRecord): string {
    return getPatientInitials(record.displayName);
  }

  /**
   * `formatDobNumeric` returns an empty string for anything it can't parse, and a blank PHI cell
   * reads as a rendering glitch staff can't tell apart from "nothing was submitted" — so it falls
   * back to the same marker the MRN and email columns use.
   */
  getDateOfBirth(record: PatientAccessRecord): string {
    return formatDobNumeric(record.dateOfBirth) || NOT_AVAILABLE;
  }

  getMedicalRecordNumber(record: PatientAccessRecord): string {
    return formatPatientMrn(record.medicalRecordNumber);
  }

  getEmailAddress(record: PatientAccessRecord): string {
    return formatEmailAddress(record.emailAddress);
  }

  private runSearch(filter: PatientFilter): void {
    // One search at a time. Two in flight would race — the older response can land last and
    // overwrite the newer rows, and the first `finalize` clears the spinner while the second is
    // still outstanding. Same guard as the linking-request patient search.
    //
    // Kept as a backstop for callers that reach here directly (`ngOnInit`). The caller that can
    // commit state before searching — `onPatientSearch` — guards at its own entry instead, so a
    // dropped search never leaves the chips describing rows it did not produce.
    if (this.isSearching()) {
      return;
    }

    this.isSearching.set(true);
    this.searchError.set(null);

    // Drop the previous page before the new one is known. Leaving it standing would caption one
    // patient's rows with another search's criteria the moment this request fails — on the screen
    // staff use to pick the right record before changing someone's portal access.
    this.results.set([]);
    this.totalRecordCount.set(null);
    this.hasSearched.set(false);

    this.searchService
      .searchPatients(filter)
      .pipe(
        finalize(() => {
          this.isSearching.set(false);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (result) => {
          this.results.set(result.patients);
          this.totalRecordCount.set(result.totalRecordCount);
          this.hasSearched.set(true);
        },
        error: (error) => {
          logHttpError('Failed to search patients for access management', error);
          this.searchError.set('Unable to search patients. Please try again.');
        },
      });
  }
}
